
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "step-1---display-the-apply-with-seek-button"
    }}>{`Step 1 - Display the Apply with SEEK button`}</h1>
    <p>{`Apply with SEEK can be used by a candidate to pre-fill in two scenarios:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`When a candidate accesses your apply form via a job advertisement on SEEK.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When a candidate accesses your apply form from a different source or directly.`}</p>
      </li>
    </ol>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/_unstable_applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` should be called each time your apply form is loaded.
It provides display assets for the Apply with SEEK button and a one-time use authorization URL that it should link to.
The authorization page prompts the candidate to share their SEEK Profile for the purpose of pre-filling your apply form.`}</p>
    <h2 {...{
      "id": "pre-fill-scenarios"
    }}>{`Pre-fill scenarios`}</h2>
    <h3 {...{
      "id": "candidate-arrives-from-seek"
    }}>{`Candidate arrives from SEEK`}</h3>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.a221a473a816f023f1614cc7b5ad67b1cfec9704.mmd.svg')} title="Full sequence" />
    <p>{`If the candidate has arrived from a `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`link-out`}</a>{` advertisement on SEEK,
you will receive a `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter appended to the query string of the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationMethod/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`https://www.example.com/job/123445/?seek-token=93WyyF1h6eaeb58ccc384b0e8353daa3011f1ece
`}</code></pre>
    <p>{`The token should be propagated in your software to the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/_unstable_applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`,
and for subsequent `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/send-completion-signal"
      }}>{`signals`}</a>{` like apply form submission.
It is unique to that specific candidate, job ad, and session,
and should not be reused outside of the context of their current application.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"query ($input: _unstable_ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton: _unstable_applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      blue\\n      pink\\n      white\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":47,\"column\":1,\"offset\":2031},\"end\":{\"line\":59,\"column\":4,\"offset\":2293},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"seekToken\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":61,\"column\":1,\"offset\":2295},\"end\":{\"line\":76,\"column\":4,\"offset\":2964},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"blue\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-blue.png\\\",\\n      \\\"pink\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-pink.png\\\",\\n      \\\"white\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-white.png\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=123def\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":78,\"column\":1,\"offset\":2966},\"end\":{\"line\":90,\"column\":4,\"offset\":3400},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"query": true,
        "($input:": true,
        "_unstable_ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton:": true,
        "_unstable_applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "blue\\n": true,
        "pink\\n": true,
        "white\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":47,\"column\":1,\"offset\":2031},\"end\":{\"line\":59,\"column\":4,\"offset\":2293},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"seekToken\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":61,\"column\":1,\"offset\":2295},\"end\":{\"line\":76,\"column\":4,\"offset\":2964},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"blue\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-blue.png\\\",\\n": true,
        "\\\"pink\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-pink.png\\\",\\n": true,
        "\\\"white\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-white.png\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "123def\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":78,\"column\":1,\"offset\":2966},\"end\":{\"line\":90,\"column\":4,\"offset\":3400},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: _unstable_ApplyWithSeekButtonInput!) {
  applyWithSeekButton: _unstable_applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      blue
      pink
      white
    }
    url
  }
}
`}</code></pre>
    <h3 {...{
      "id": "candidate-arrives-from-another-source"
    }}>{`Candidate arrives from another source`}</h3>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.dc6af2e5e963bd49c297e6f1385a9c909550ff12.mmd.svg')} title="Full sequence" />
    <pre><code parentName="pre" {...{
        "className": "language-graphql",
        "metastring": "lovato-ignore-runnable-graphql-operation",
        "lovato-ignore-runnable-graphql-operation": true
      }}>{`query ($input: _unstable_ApplyWithSeekButtonInput!) {
  applyWithSeekButton: _unstable_applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      blue
      pink
      white
    }
    url
  }
}
`}</code></pre>
    <p>{`Where a candidate has arrived at your application form from another source
(direct link, company career site, etc.),
the `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter will not be present in the query string and should be omitted from the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/_unstable_applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"query ($input: _unstable_ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton: _unstable_applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      blue\\n      pink\\n      white\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":124,\"column\":1,\"offset\":4339},\"end\":{\"line\":136,\"column\":4,\"offset\":4601},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The HTTPS URL of your apply form for the specific position.\\n    \\\"applicationUri\\\": \\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n    // An optional hirer-provided opaque job reference.\\n    \\\"seekHirerJobReference\\\": \\\"REQ-65558\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":138,\"column\":1,\"offset\":4603},\"end\":{\"line\":156,\"column\":4,\"offset\":5338},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"blue\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-blue.png\\\",\\n      \\\"pink\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-pink.png\\\",\\n      \\\"white\\\": \\\"https://integration.seek.com/some-path-for-assets/images/awsk-white.png\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=123def\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":158,\"column\":1,\"offset\":5340},\"end\":{\"line\":170,\"column\":4,\"offset\":5774},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"query": true,
        "($input:": true,
        "_unstable_ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton:": true,
        "_unstable_applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "blue\\n": true,
        "pink\\n": true,
        "white\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":124,\"column\":1,\"offset\":4339},\"end\":{\"line\":136,\"column\":4,\"offset\":4601},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://partner.example.com/jobad/00000001/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "your": true,
        "apply": true,
        "form": true,
        "specific": true,
        "\\\"applicationUri\\\":": true,
        "An": true,
        "optional": true,
        "hirer-provided": true,
        "opaque": true,
        "job": true,
        "reference.\\n": true,
        "\\\"seekHirerJobReference\\\":": true,
        "\\\"REQ-65558\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":138,\"column\":1,\"offset\":4603},\"end\":{\"line\":156,\"column\":4,\"offset\":5338},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"blue\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-blue.png\\\",\\n": true,
        "\\\"pink\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-pink.png\\\",\\n": true,
        "\\\"white\\\":": true,
        "\\\"https://integration.seek.com/some-path-for-assets/images/awsk-white.png\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "123def\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":158,\"column\":1,\"offset\":5340},\"end\":{\"line\":170,\"column\":4,\"offset\":5774},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: _unstable_ApplyWithSeekButtonInput!) {
  applyWithSeekButton: _unstable_applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      blue
      pink
      white
    }
    url
  }
}
`}</code></pre>
    <h2 {...{
      "id": "content-localisation"
    }}>{`Content localisation`}</h2>
    <p>{`Your software should include the standard `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language"
      }}><inlineCode parentName="a">{`Accept-Language`}</inlineCode></a>{` HTTP request header when it executes the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/_unstable_applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`.
We support multiple brands and languages across our markets,
so providing accurate country information and language preferences enables us to provide a more familiar experience for the candidate.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "/graphql/in-practice#content-localisation"
      }}>{`content localisation`}</a>{` for more information.`}</p>
    <h2 {...{
      "id": "content-security-policy-compatibility"
    }}>{`Content Security Policy compatibility`}</h2>
    <p>{`This section is for integration partners who enforce strict content security policies in their web-based software.`}</p>
    <p><a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP"
      }}>{`Content Security Policy`}</a>{` (CSP) is a security feature that helps prevent various types of attacks, including Cross-Site Scripting (XSS) and data injection attacks.
Add the following directive to your CSP to load Apply with SEEK assets from the SEEK API:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`Content-Security-Policy: img-src 'self'
https://integration.seek.com/some-path-for-assets/images/;
`}</code></pre>
    <p>{`This sample directive instructs the browser to only load images from your own domain (`}<inlineCode parentName="p">{`self`}</inlineCode>{`) and our `}<inlineCode parentName="p">{`images`}</inlineCode>{` directory.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;